import { useState } from "react";
import { useSelector } from "react-redux";

import { Trans, useTranslation } from "react-i18next";

import { Delete, Speed } from "@mui/icons-material";
import { Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Slider, Typography } from "@mui/material";

import apiClient from "../../../../services/api";
import store from "../../../../store";
import { toggleMessageOpen } from "../../../../store/actions/Message";
import { CustomLoadingScreen } from "../../../partials/LoadingScreen";
import { setSelectedProject } from "../../../../store/actions/Project";

const textureResolutions = [256, 512, 1024, 2048];

function OptimizeTab()
{
    const { t } = useTranslation();
    const selectedProject = useSelector((state) => state.project.selectedProject);

    const [loading, setLoading] = useState(false);
    const [compressionLevel, setCompressionLevel] = useState(
        selectedProject?.project?.compression_level
    );
    const [maxTextureResolution, setMaxTextureResolution] = useState(
        selectedProject?.project?.max_texture_resolution ? selectedProject?.project?.max_texture_resolution : 'original'
    );

    function valueText(value) {
        return `${value}`;
    }

    const handleOptimize = () => {
        setLoading(true);

        const formData = {
            id: selectedProject?.project?.id,
            compressionLevel: compressionLevel,
            maxTextureResolution: maxTextureResolution
        };

        apiClient.post('/api/projects/optimize', formData).then( response => {
            if(response.data.success) {
                store.dispatch(toggleMessageOpen(t('Assets optimized successfully!'), 'success'));

                const data = selectedProject;

                data.project.compression_level = compressionLevel;
                data.project.max_texture_resolution = maxTextureResolution;

                setSelectedProject(data);
            } else {
                store.dispatch(toggleMessageOpen(t('An error occurred when trying to optimize assets!'), 'success'));
            }
            setLoading(false);
        }).catch( error => {
            store.dispatch(toggleMessageOpen(error, "error"));
            setLoading(false);
        });
    }

    const handleRemoveOptimizedAssets = () => {
        setLoading(true);

        apiClient.post('/api/projects/delete-optimize', {id: selectedProject?.project?.id}).then( response => {
            if(response.data.success) {
                store.dispatch(toggleMessageOpen(t('Assets removed successfully!'), 'success'));

                const data = selectedProject;

                data.project.compression_level = null;
                data.project.max_texture_resolution = null;

                setCompressionLevel(null);
                setMaxTextureResolution('original');

                setSelectedProject(data);
            } else {
                store.dispatch(toggleMessageOpen(t('An error occurred when trying to remove optimize assets!'), 'success'));
            }
            setLoading(false);
        }).catch( error => {
            store.dispatch(toggleMessageOpen(error, "error"));
            setLoading(false);
        });
    }

    const handleChangeCompressionLevel = (event) => {
        setCompressionLevel(event.target.value === '' ? 0 : Number(event.target.value));
    }

    const handleChangeMaxResolution = (event) => {
        setMaxTextureResolution(event.target.value);
    }

    const areInputsDisabled = () => {
        return loading ? true : false;
    }

    return (
        <Box sx={{p: 3}}>
            <Box sx={{mb: 1}}>
                <Slider
                    aria-label={t('Compression level')}
                    getAriaValueText={valueText}
                    value={compressionLevel}
                    valueLabelDisplay="on"
                    step={10}
                    marks
                    min={0}
                    max={100}
                    onChange={handleChangeCompressionLevel}
                    disabled={areInputsDisabled()}
                />
                <Typography><Trans>Compression level</Trans></Typography>
            </Box>
            <Box sx={{mb: 1}}>
                <Typography><Trans>Max Texture Resolution</Trans></Typography>
                <FormControl>
                    <RadioGroup
                        value={maxTextureResolution}
                        disabled={areInputsDisabled()}
                        onChange={handleChangeMaxResolution}
                        name="max-texture-resolution"
                    >
                        <FormControlLabel
                            value="original"
                            control={<Radio sx={{'& .MuiSvgIcon-root': {fontSize: 20}}} />}
                            label="Original"
                        />
                        {
                            textureResolutions.map((resolution) => (
                                <FormControlLabel
                                    value={resolution}
                                    control={<Radio sx={{'& .MuiSvgIcon-root': {fontSize: 20}}} />}
                                    label={resolution}
                                />
                            ))
                        }
                    </RadioGroup>
                </FormControl>
            </Box>
            {loading && <CustomLoadingScreen text={t('Optimizing assets, please wait ..')} />}
            {
                selectedProject?.project?.compression_level || selectedProject?.project?.max_texture_resolution
                    ? (
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => {handleRemoveOptimizedAssets()}}
                            sx={{mt: 1}}
                        >
                            <Trans>Remove optimized assets</Trans>
                        </Button>
                    )
                    : (
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<Speed />}
                            onClick={() => {handleOptimize()}}
                            sx={{mt: 1}}
                            disabled={areInputsDisabled()}
                        >   
                            <Trans>Optimize</Trans>
                        </Button>
                    )
            }
        </Box>
        
    );
}

export default OptimizeTab;